import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { CASES } from "@/lib/content";
import { MagneticButton } from "../site/Buttons";
import { SectionLabel, RevealLines, FadeUp } from "../site/motion";

const CaseFrame = ({ c }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const clip = useTransform(scrollYProgress, [0, 0.35], ["inset(14% 10% 14% 10% round 32px)", "inset(0% 0% 0% 0% round 24px)"]);
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <Link to={`/work/${c.key}`} ref={ref} data-testid={`case-${c.key}-frame-link`} className="group relative block">
      <motion.div style={{ clipPath: clip }} className="relative h-[56vh] overflow-hidden md:h-[78vh]">
        <span className="absolute right-6 top-6 z-10 flex h-24 w-24 scale-75 items-center justify-center rounded-full bg-cream font-mono text-[10px] uppercase tracking-[0.2em] text-skaris-ink opacity-0 transition-[opacity,transform] duration-500 group-hover:scale-100 group-hover:opacity-100 md:right-12 md:top-12 md:h-32 md:w-32">View case</span>
        <motion.img src={c.img} alt={c.name} style={{ y }} className="duotone absolute inset-0 h-[120%] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-skaris-ink via-skaris-ink/30 to-transparent" />
        <div className="spot-light absolute inset-0" />
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-skaris-sky">{c.kicker}</p>
          <h3 className="mt-3 font-display text-6xl font-semibold uppercase leading-[0.85] tracking-[-0.04em] text-cream md:text-[10rem]">{c.name}</h3>
        </div>
      </motion.div>
    </Link>
  );
};

const CaseBody = ({ c }) => (
  <div className="mt-10 grid gap-10 lg:grid-cols-12">
    <FadeUp className="lg:col-span-4">
      <p className="font-display text-2xl font-medium leading-snug text-cream">{c.tagline}</p>
      <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">{c.meta}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {c.stack.map((s) => <span key={s} className="rounded-full border border-skaris-sky/40 px-3 py-1 font-mono text-[11px] text-cream/85">{s}</span>)}
      </div>
      <div className="mt-8"><MagneticButton to={`/work/${c.key}`} variant="ghost" testid={`case-${c.key}-read-more`}>Read the case study</MagneticButton></div>
    </FadeUp>
    <FadeUp delay={0.1} className="grid gap-8 sm:grid-cols-2 lg:col-span-5">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-skaris-sky">Problem</p>
        <p className="mt-3 font-body text-sm leading-relaxed text-cream/80">{c.problem}</p>
      </div>
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-skaris-sky">Solution</p>
        <p className="mt-3 font-body text-sm leading-relaxed text-cream/80">{c.solution}</p>
      </div>
    </FadeUp>
    <FadeUp delay={0.2} className="grid grid-cols-2 gap-px self-start overflow-hidden rounded-2xl border border-cream/10 bg-cream/10 lg:col-span-3">
      {c.stats.map((s) => (
        <div key={s.l} data-testid={`case-${c.key}-stat`} className="bg-skaris-ink p-5">
          <p className="font-display text-2xl font-semibold text-cream">{s.v}</p>
          <p className="mt-1 font-body text-[12px] leading-snug text-cream/60">{s.l}</p>
        </div>
      ))}
    </FadeUp>
  </div>
);

export const Work = () => (
  <section id="work" data-testid="work-section" className="relative py-24 md:py-32">
    <div className="mx-auto max-w-[1440px] px-6 md:px-10">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <SectionLabel n="(04)">Selected work</SectionLabel>
          <RevealLines inView as="h2" lines={["Shipped, live,", "and measured."]}
            className="mt-6 font-display text-4xl font-semibold uppercase leading-[0.95] tracking-[-0.03em] text-cream sm:text-5xl lg:text-7xl"
            lineClass={(i) => (i === 1 ? "text-cream/55" : "")} />
        </div>
        <p className="max-w-xs font-body text-sm text-cream/70">Two products we architected and built end-to-end — from first workshop to App Store and court-side tablet.</p>
      </div>
      <div className="mt-16 space-y-28 md:space-y-40">
        {CASES.map((c) => (
          <article key={c.key} data-testid={`case-${c.key}`}>
            <CaseFrame c={c} />
            <CaseBody c={c} />
          </article>
        ))}
      </div>
    </div>
  </section>
);
